"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Eye,
  Sparkles,
  Crown,
  Target,
  Shield,
  TrendingUp,
  Building2,
  Crosshair,
  Award,
  ArrowRight,
  Mic,
  BookOpen,
  CheckCircle2,
  GitBranch,
  Lock,
  Users,
  Compass,
  Briefcase,
  ShieldCheck,
  Layers,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TimelineBlock } from "@/lib/blocks/timeline-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";

const BUILD_TIMELINE = [
  { period: "Phase 1", title: "Foundation & Architecture", body: "Define the operating principles. Codify the protocols. Build the systems that outlast individuals. The infrastructure that took a decade to build cannot be shortcut by publicity.", icon: <Layers className="size-4" />, accent: "denim" as const },
  { period: "Phase 2", title: "Capability Development", body: "Accumulate the expertise that cannot be replicated without the same investment. Data centers, mainframes, supercomputers, nine proprietary platforms — each capability a structural moat.", icon: <ShieldCheck className="size-4" />, accent: "red" as const },
  { period: "Phase 3", title: "Operational Discipline", body: "Run the protocol. Keep the judgment. Measure outcomes rather than effort. The operating system that produces sovereignty cannot be announced before it is built.", icon: <Crosshair className="size-4" />, accent: "cyan" as const },
  { period: "Phase 4", title: "Quiet Delivery", body: "Ship the outcome. Let the result generate recognition. Resist the temptation to announce before the evidence is in. The organizations that endure deliver first and document second.", icon: <Eye className="size-4" />, accent: "purple" as const },
  { period: "Phase 5", title: "Sovereign Validation", body: "Test every claim under conditions where the margin for error is zero. The sovereign client is the ultimate verification — governments, royal families, and defense agencies that select by capability.", icon: <Crown className="size-4" />, accent: "amber-gold" as const },
  { period: "Phase 6", title: "Durability Compounding", body: "Let the architecture compound. The competitive advantage is structural, not visible. A decade of investment cannot be replicated by a competitor in two years, regardless of publicity budget.", icon: <TrendingUp className="size-4" />, accent: "emerald" as const },
];

const VISIBILITY_PATHOLOGIES = [
  { title: "Signaling Over Substance", body: "The visibility economy rewards the performance of work rather than the completion of work. The narrative becomes the product. The audience becomes the client. The metrics of success become engagement rates rather than revenue.", icon: <Target className="size-5" />, accent: "red" as const },
  { title: "Speed Over Durability", body: "The news cycle rewards disruption — the rapid ascent, the viral moment, the overnight success. It does not reward the decade of building that produces an organization capable of serving governments and defense agencies.", icon: <TrendingUp className="size-5" />, accent: "purple" as const },
  { title: "Charisma Over Systems", body: "The visibility ecosystem rewards the individual leader. The organization that depends on individual visibility for its reputation is fragile — it collapses when the individual departs. Systems are invisible. Systems are also what produce outcomes.", icon: <Users className="size-5" />, accent: "cyan" as const },
  { title: "Premature Publicity", body: "Organizations that announce before they deliver create expectations they cannot meet. The audience remembers the announcement longer than the delivery. The credibility cost compounds.", icon: <Mic className="size-5" />, accent: "amber-gold" as const },
  { title: "Attention Fragmentation", body: "Leaders who cultivate personal brands discover that the brand becomes a cage — demanding constant content creation, continuous public engagement, and perpetual visibility that consumes the energy required for actual work.", icon: <Eye className="size-5" />, accent: "denim" as const },
  { title: "Metric Confusion", body: "Followers, impressions, media mentions, conference invitations — the metrics of visibility are mistaken for the metrics of impact. The conflation is understandable. It is also operationally dangerous.", icon: <Compass className="size-5" />, accent: "emerald" as const },
];

const FAQ_ITEMS = [
  { q: "What is \"Build in the Dark\" about?", a: "The signature keynote on the discipline of building consequential things without the performative visibility the attention economy demands. The three disciplines: outcome accountability over effort signaling, duration over disruption, and systems over charisma. Drawn from a decade of sovereign-scale operations across eighteen countries." },
  { q: "Is this keynote against all visibility?", a: "No. The keynote argues against the confusion of visibility with substance. When visibility is a byproduct of impact, it is valuable. When visibility substitutes for impact, it is destructive. The discipline is knowing which is which." },
  { q: "How is this different from anti-marketing or humility keynotes?", a: "The argument is operational, not moral. The evidence is drawn from sovereign-scale operations where discretion is a contractual requirement, not a stylistic choice. The keynote is not about modesty — it is about the competitive advantage of building capabilities that publicity cannot create." },
  { q: "How long is the standard keynote?", a: "Thirty-five to fifty minutes for the standard keynote. Fireside chat runs thirty to forty-five minutes. Executive briefing runs sixty to ninety minutes. Panel contribution runs twenty to thirty minutes. All formats are available virtually and in person." },
  { q: "What audiences benefit most from this keynote?", a: "Entrepreneurs and startup founders, private company leaders and family office executives, sovereign and defense sector leaders, leadership forums seeking intellectual rigor, cybersecurity and technology summits, and corporate strategy and M&amp;A forums evaluating competitive durability." },
  { q: "Can the executive briefing be confidential?", a: "Yes. The executive briefing is designed for confidential application of the three-discipline framework — diagnosing where the organization allocates energy toward visibility versus outcomes, where capabilities are structural versus superficial, and where systems depend on individuals rather than architecture. The format is private and invitation-only." },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const THREE_DISCIPLINES = [
  {
    num: "01",
    icon: Crosshair,
    title: "Outcome Accountability",
    body: "Measure what was delivered, not what was announced. In sovereign-grade operations, this discipline is absolute. Every platform is measured by the result it delivers. The platform either achieved the outcome it was designed to achieve, or it was redesigned until it did. There is no metric for tried hard. There is no allowance for meant well.",
    accent: "denim",
  },
  {
    num: "02",
    icon: TrendingUp,
    title: "Duration Over Disruption",
    body: "The discipline of playing a longer game than the attention economy rewards. Duration recognizes that the most durable competitive advantages are built over years, not months. The infrastructure that took a decade to build cannot be replicated by a competitor in two years, regardless of their publicity budget.",
    accent: "purple",
  },
  {
    num: "03",
    icon: Building2,
    title: "Systems Over Charisma",
    body: "The discipline of designing organizations that produce results through operating architecture rather than through individual visibility. The attention economy rewards the individual leader. Systems reward the architecture — producing consistent outcomes regardless of who is recognized for building them.",
    accent: "cyan",
  },
];

const ATTENTION_PROBLEMS = [
  { title: "Signaling over substance", body: "The visibility economy rewards the performance of work rather than the completion of work. Founders learn to narrate their journey rather than to deliver outcomes. The narrative becomes the product. The audience becomes the client. Engagement rates become the metric of success — not revenue, not customer satisfaction, not operational capability.", accent: "denim" },
  { title: "Speed over durability", body: "The news cycle rewards disruption — the rapid ascent, the viral moment, the overnight success. It does not reward the decade of building that produces an organization capable of serving governments and defense agencies across eighteen countries. The attention economy has no metric for durability.", accent: "purple" },
  { title: "Charisma over systems", body: "The visibility ecosystem rewards the individual — the founder whose personal brand drives attention, the CEO whose public profile attracts investment. It does not reward the system — the operating architecture that produces consistent outcomes regardless of who is in the room. The result is organizations that depend on individual visibility for their reputation, and that collapse when the individual departs.", accent: "cyan" },
];

const EVIDENCE = [
  { metric: "100", suffix: "%", label: "Client Satisfaction", sub: "300+ elite clients", accent: "denim" },
  { metric: "15/18", suffix: "", label: "Campaign Victories", sub: "Eighty-three point three percent", accent: "purple" },
  { metric: "99.9999", suffix: "%", label: "Uptime", sub: "31.5 sec max/year", accent: "cyan" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years sovereign", accent: "emerald" },
];

const TAKEAWAYS = [
  { title: "Publicity is not impact.", body: "The distinction is operational, not philosophical. Audit the organization&apos;s energy allocation: how much time, money, and effort is invested in visibility versus outcomes? The ratio should favor outcomes — always.", accent: "denim" },
  { title: "Measure what was delivered, not what was announced.", body: "Replace activity metrics with outcome metrics. Instead of tracking hours, tasks, posts, and engagement rates, track client satisfaction, operational uptime, delivery quality, and security incident rates.", accent: "purple" },
  { title: "Build capabilities that cannot be replicated in two years.", body: "Capabilities that can be replicated quickly are not durable advantages. Durable advantages are structural: they require years of investment, accumulated expertise, and operational architecture that cannot be shortcut.", accent: "cyan" },
  { title: "Design systems that outlast individuals.", body: "If the outcome depends on the individual — on their personal brand, their public profile, their individual expertise — the organization has a charisma dependency, not an operating system. Document the processes, protocols, and decision frameworks.", accent: "emerald" },
  { title: "Let outcomes generate visibility.", body: "The discipline is patience: build the outcome first, and let the outcome generate the recognition. The alternative — announcing before delivering — produces temporary attention and long-term credibility loss.", accent: "amber-gold" },
];

const AUDIENCE = [
  { icon: Crown, title: "Entrepreneurs & Founders", body: "Founders building organizations who face constant pressure to build in public, cultivate personal brands, and generate visibility — the counter-narrative with the evidence base.", accent: "denim" },
  { icon: Lock, title: "Private Company Leaders", body: "Leaders of privately held organizations that do not need public visibility to attract customers — who serve clients through capability rather than reputation.", accent: "purple" },
  { icon: ShieldCheck, title: "Sovereign & Defense Sector", body: "Decision-makers operating in environments where discretion is not a preference but a requirement — where confidentiality is contractual, where security is existential.", accent: "cyan" },
  { icon: Briefcase, title: "Leadership Forums", body: "Events seeking a keynote that challenges the prevailing entrepreneurial narrative — that offers evidence rather than inspiration, framework rather than motivational stimulus.", accent: "emerald" },
  { icon: Shield, title: "Cybersecurity Summits", body: "Organizations operating in technology sectors where security, discretion, and operational integrity are competitive advantages — where the most valuable capabilities are the ones that cannot be seen.", accent: "amber-gold" },
  { icon: Building2, title: "Corporate Strategy Forums", body: "Decision-makers evaluating competitive advantages, organizational durability, and long-term value creation. The duration-over-disruption framework applies to strategic planning.", accent: "red" },
];

const FORMATS = [
  { label: "Standard Keynote", duration: "35–50 min", body: "Structured presentation from core argument through the attention economy problem to the three disciplines and actionable takeaways.", accent: "denim" },
  { label: "Fireside Chat", duration: "30–45 min", body: "The most effective format for this topic. Conversational exploration of the personal decisions, operational trade-offs, and sovereign-scale evidence.", accent: "purple" },
  { label: "Executive Briefing", duration: "60–90 min", body: "Private session applying the three-component framework as a diagnostic — identifying where the organization allocates energy toward visibility versus outcomes.", accent: "cyan" },
  { label: "Panel Contribution", duration: "20–30 min", body: "Focused contribution to multi-speaker discussions on entrepreneurship, organizational durability, or competitive strategy.", accent: "emerald" },
  { label: "Workshop", duration: "2–3 hrs", body: "Hands-on application of the three disciplines to the organization&apos;s specific competitive environment.", accent: "amber-gold" },
  { label: "Virtual Delivery", duration: "All formats", body: "All formats available for virtual events with the same operational authority and engagement quality.", accent: "red" },
];

const GAINS = [
  "A framework, not motivation — three operational disciplines: outcome accountability, duration over disruption, systems over charisma",
  "The countercultural evidence — the most durable outcomes were produced by organizations that built in the dark",
  "Sovereign-scale perspective — what building looks like when publicity is not an option",
  "Immediate applicability — the diagnostic: where is the organization allocating energy toward visibility rather than outcomes?",
  "Cross-domain insight — the physician who runs technology operations, the operator who understands sovereign-scale stakes",
  "The three disciplines of building without an audience — applied to the specific competitive context",
];

export default function BuildInTheDarkPage() {
  return (
    <>
      <PageHero
        icon={Eye}
        overline="Signature Keynote"
        overlineClassName="text-denim"
        title={
          <>
            Publicity Is Not Impact. <span className="text-gradient-premium">Outcomes Are.</span>
          </>
        }
        subtitle="Build in the Dark is the signature keynote on the discipline of building consequential things without the performative visibility the attention economy demands. The operating philosophy that produced one-hundred-percent client satisfaction, fifteen out of eighteen political campaigns won, and zero security incidents — none of it announced, none of it performed, all of it measured by outcomes."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Speaking", href: "/speaking/" }, { label: "Build in the Dark" }]}
        badge="The operator's discipline"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Abstract</span>
          </div>
          <h2 className="text-display-md mb-5">The operator&apos;s discipline in an attention economy.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The world rewards visibility. The organizations that endure reward outcomes. The keynote addresses the most seductive myth in modern entrepreneurship: that visibility equals value. The attention economy has created an ecosystem where founders are encouraged to narrate every milestone, where executives are expected to perform thought leadership on social media, and where organizations are pressured to announce before they have shipped.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The result is an entrepreneurial culture that optimizes for appearance over substance, for speed over durability, and for recognition over outcomes. The keynote dismantles this myth with operational evidence — drawn from a decade of building and running a sovereign-grade digital conglomerate while maintaining a public profile proportional to the work, not performative relative to it.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Target className="size-3.5 text-denim" />
            <span className="text-overline text-denim">The Core Argument</span>
          </div>
          <h2 className="text-display-md mb-4">Publicity is not impact.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The central thesis is as simple as it is countercultural. The two are frequently conflated — because the attention economy rewards visibility, and because the metrics of visibility are easier to measure than the metrics of impact. The conflation is operationally dangerous.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {EVIDENCE.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Attention Economy</span>
          </div>
          <h2 className="text-display-md mb-4">Why the modern ecosystem rewards the wrong things.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Three pathologies the attention economy produces in entrepreneurial culture. Each one is a structural incentive, not a personal failing.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {ATTENTION_PROBLEMS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <span className="text-caption font-bold text-white">{i + 1}</span>
                    </div>
                    <h3 className={`text-heading-sm ${a.text}`}>{p.title}</h3>
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: p.body }} />
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-4`}>
            <Building2 className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Three Disciplines</span>
          </div>
          <h2 className="text-display-md mb-4">The operating framework for quiet building.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Three disciplines, together, form the operating framework for building consequential organizations without performative visibility. Each demonstrated through sovereign-scale operational evidence.
          </p>
        </div>
        <div className="space-y-4 max-w-4xl mx-auto">
          {THREE_DISCIPLINES.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group glass-card rounded-2xl p-6 lg:p-7 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-6 items-center">
                  <div className={`size-16 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring} group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                    <d.icon className="size-7 text-white" />
                  </div>
                  <div>
                    <span className={`text-overline ${a.text}`}>Discipline {d.num}</span>
                    <h3 className={`text-display-sm mb-2 ${a.text}`}>{d.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{d.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Key Takeaways</span>
          </div>
          <h2 className="text-display-md mb-4">Five actionable insights for tomorrow.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Specific, implementable actions that transform the organization&apos;s operating method starting the next business day.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3 max-w-5xl mx-auto">
          {TAKEAWAYS.map((t, i) => {
            const accents = ["denim", "purple", "cyan", "emerald", "amber-gold"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-sm mb-1 ${a.text}`}>{t.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: t.body }} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Users className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Who This Keynote Serves</span>
          </div>
          <h2 className="text-display-md mb-4">Ideal audience.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The keynote resonates most with founders, operators, and leaders who recognize that the prevailing entrepreneurial narrative — build in public, cultivate personal brand, optimize for visibility — has not produced the outcomes it promised.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCE.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-sm mb-1.5 ${ac.text}`}>{a.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Briefcase className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">Format Options</span>
            </div>
            <h2 className="text-display-md mb-4">Flexibility for every event.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Six delivery formats, with the fireside chat particularly effective for this topic — conversational depth that allows for the personal decisions, operational trade-offs, and sovereign-scale evidence to be explored with nuance.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FORMATS.map((f, i) => {
              const a = ACCENT[f.accent];
              return (
                <motion.div
                  key={f.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className={`text-heading-sm ${a.text}`}>{f.label}</h3>
                    <span className={`text-caption font-bold ${a.text}`}>{f.duration}</span>
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: f.body }} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Award className="size-3.5 text-lime" />
            <span className="text-overline text-lime">What Attendees Gain</span>
          </div>
          <h2 className="text-display-md mb-4">The value of this keynote experience.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {GAINS.map((g, i) => {
            const accents = ["denim", "purple", "cyan", "emerald", "amber-gold", "red"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={g}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <CheckCircle2 className="size-4 text-white" />
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed flex-1">{g}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-denim/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Eye className="size-10 text-denim mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Book this keynote.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Build in the Dark is the philosophical cornerstone of the speaking portfolio. The operating disposition that enables the application of every other topic. The discipline of measuring by outcomes rather than visibility.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Book the Keynote
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/speaking/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                All Speaking Topics
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Signature Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">The Build Process Timeline.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            Six phases of disciplined quiet building. Each phase compounds into the next. The process cannot be shortcut by publicity — only by execution. The timeline maps the operating rhythm of an organization that has produced sovereign-scale outcomes across eighteen countries while most of the world has never heard its name.
          </p>
        </div>
        <TimelineBlock milestones={BUILD_TIMELINE} orientation="horizontal" />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">The Attention Economy Problem</span>
          </div>
          <h2 className="text-display-md mb-4">Three pathologies the attention economy produces.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The structural incentive problem that distorts entrepreneurial decision-making. Each pathology produces a specific failure pattern. Each failure is preventable through the discipline of building in the dark.
          </p>
        </div>
        <FeatureGridBlock features={VISIBILITY_PATHOLOGIES} columns={3} />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-5`}>
            <Lock className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Why Publicity Is Not Impact</span>
          </div>
          <h2 className="text-display-md mb-4">The visibility trap in modern entrepreneurship.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            Founders who have built substantial organizations are told they need to "build in public" to attract investment. Executives who have delivered verified outcomes are told they need personal brands to advance their careers. Organizations that have produced sovereign-scale results are told they need public profiles to win clients. The advice is consistent, well-intentioned, and operationally wrong.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The root cause is the attention economy&apos;s incentive structure. Social media platforms reward content creation. Conference circuits reward speakers with large followings. Media ecosystems reward stories that generate engagement. Investment ecosystems reward founders who can narrate their journey compellingly. The result is an ecosystem where the metrics of visibility — followers, impressions, media mentions, conference invitations — are mistaken for the metrics of impact — outcomes delivered, capabilities built, durability achieved.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The damage is measurable. Founders who invest in visibility over capability discover, when crises arrive, that followers do not produce outcomes and media mentions do not prevent operational failures. Organizations that announce before they deliver discover that premature publicity creates expectations they cannot meet. Leaders who cultivate personal brands discover that the brand becomes a cage — demanding constant content creation, continuous public engagement, and perpetual visibility that consumes the energy required for actual work. The discipline of building in the dark is the discipline of refusing that substitution.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <AccordionBlock
          items={FAQ_ITEMS}
          eyebrow="Frequently Asked Questions"
          title="What decision-makers ask before booking."
          description="The questions that surface most often in preliminary conversations. Each answer reflects the operating reality, not the marketing narrative."
        />
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "The Triple Threat", description: "The cross-domain integration that demands discretion — medicine, technology, politics.", href: "/speaking/the-triple-threat/", icon: GitBranch, accent: "amber-gold" },
          { title: "How Excellence Actually Runs", description: "The system that quiet building sustains — protocols, checklists, reviews, accountability.", href: "/speaking/how-excellence-actually-runs/", icon: Award, accent: "red" },
          { title: "Women Breaking Barriers", description: "The discipline of measuring by outcomes rather than recognition — the operating reality of women at the top.", href: "/speaking/women-breaking-barriers/", icon: Users, accent: "coral" },
          { title: "The 70% Project", description: "The talent strategy that operates independently of public recognition — teams built on performance signals.", href: "/speaking/the-70-percent-project/", icon: Layers, accent: "emerald" },
        ]}
      />
    </>
  );
}
